import React, { ReactNode, useState } from 'react';
import classNames from 'classnames';

import BaseCell from '../BaseCell';
import EllipsisMenu from '../../../EllipsisMenu';
import type { EllipsisMenuItem } from '../../../EllipsisMenu';
import Layout from '../../../Layout';

/** Props for the table action icon cell. Renders a small-32 gray ellipsis-h icon button that opens the menu. */
export interface ActionIconCellProps {
  menuItems: EllipsisMenuItem[];
  disabled?: boolean;
  onMenuChange?: (menuOpen: boolean) => void;
  menuItemHoverColor?: 'gray-50' | 'blue-50';
  iconPosition?: 'left' | 'right';
  /** Cell width from table layout; used for alignment. */
  width?: number;
  /** Optional control(s) rendered before the ellipsis (e.g. row-hover actions). */
  leadingContent?: ReactNode;
}

/**
 * Action icon cell for NewTable: size small-32 icon button, default icon ellipsis-h, icon button style gray.
 * Use for columns with cellContentType: 'action'. Set padding via column.paddingVariant or cellContentType.
 */
const ACTION_ICON_CELL_CONTENT_CLASS = 'NewTable-actionIconCellContent';

const ActionIconCell: React.FC<ActionIconCellProps> = ({
  menuItems,
  disabled,
  onMenuChange,
  menuItemHoverColor = 'blue-50',
  iconPosition = 'left',
  width,
  leadingContent
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const handleMenuChange = (open: boolean) => {
    setMenuOpen(open);
    onMenuChange?.(open);
  };

  return (
    <Layout
      direction="row"
      align="center"
      justify="flex-end"
      gap={8}
      __style={width !== undefined ? { width } : undefined}
    >
      {leadingContent}
      <BaseCell>
        <span
          className={classNames(ACTION_ICON_CELL_CONTENT_CLASS, {
            'is-menuOpen': menuOpen
          })}
        >
          <EllipsisMenu
            menuItems={menuItems}
            size="sm"
            buttonType="gray"
            iconName="ellipsisH"
            disabled={disabled}
            onMenuChange={handleMenuChange}
            menuItemHoverColor={menuItemHoverColor}
            iconPosition={iconPosition}
          />
        </span>
      </BaseCell>
    </Layout>
  );
};

export default ActionIconCell;
